import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { presetRange, type DashRange } from '@/lib/sales-metrics';
import { salesDashboardService } from '@/services/salesDashboard.service';
import { QUERY_KEYS } from '@/utils/constants';

const DAY_MS = 86_400_000;

// A DashRange to an absolute [from, to] window, including the whole `to` day
// (to + 1 day - 1ms) - the exact window the old client-side filterByRange used,
// preserved so moving the aggregation server-side doesn't shift range edges.
// This is the ONLY place that extension happens: `to` reaches the API as an
// inclusive instant and salesDashboard.service.ts uses it verbatim. Extending
// it on both sides would run every range a full day long.
//
// Note the arithmetic is blind - it adds a day to whatever `to` holds. For a
// picked end date (midnight) that lands on the intended 23:59:59.999, but the
// `to = new Date()` presets (All Time, This Year, FYTD, the pending presets)
// end up ~24h in the future. Harmless only because created_at/sold_at are
// always stamped at write time, so no future-dated row exists to be pulled in;
// revisit this if back-dated or imported sales are ever added.
const windowOf = (r: DashRange): { from: string; to: string } => ({
  from: r.from.toISOString(),
  to: new Date(r.to.getTime() + DAY_MS - 1).toISOString(),
});

export const useDashboardMetrics = (range: DashRange) => {
  const w = windowOf(range);
  return useQuery({
    queryKey: QUERY_KEYS.SALES.DASHBOARD_METRICS(w.from, w.to),
    queryFn: () => salesDashboardService.getMetrics(w),
    placeholderData: keepPreviousData,
  });
};

// Separate query/window from the metrics so changing the pending-work date filter
// doesn't re-run the whole dashboard aggregation (review #2).
export const useDashboardPending = (pendingRange: DashRange) => {
  const w = windowOf(pendingRange);
  return useQuery({
    queryKey: QUERY_KEYS.SALES.DASHBOARD_PENDING(w.from, w.to),
    queryFn: () => salesDashboardService.getPending(w),
    placeholderData: keepPreviousData,
  });
};

// Monthly/yearly target revenue for the current period, independent of the
// dashboard's date filter, so the target cards stay accurate even when the main
// dashboard shows "All Time" or a custom range. The month/year START instants
// are computed browser-side (same basis as the This Month/This Year presets) and
// passed to the server, so the target figures share one timezone with the rest
// of the dashboard. Both starts are stable within a session, so the query key is
// stable (it only changes when the month/year rolls over).
export const useTargetProgress = () => {
  const monthStart = presetRange('month').from.toISOString();
  const yearStart = presetRange('year').from.toISOString();
  return useQuery({
    queryKey: QUERY_KEYS.SALES.TARGET_PROGRESS(monthStart, yearStart),
    queryFn: () => salesDashboardService.getTargetProgress({ monthStart, yearStart }),
  });
};
